import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, UserPlus, MoreVertical, Mail } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/users").then(r => setUsers(r.data)).catch(()=>{});
    api.get("/roles").then(r => setRoles(r.data)).catch(()=>{});
  };
  useEffect(load, []);

  const updateUser = async (uid, patch) => {
    try {
      await api.patch(`/users/${uid}`, patch);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deactivate = async (uid) => {
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Deactivated");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <Layout
      title="Team"
      subtitle="Members &amp; permissions"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-10 px-4 rounded-xl btn-warm text-[13px] font-semibold" data-testid="invite-button">
              <UserPlus className="h-4 w-4 mr-1.5" /> Invite member
            </Button>
          </DialogTrigger>
          <InviteDialog roles={roles} onCreated={() => { setOpen(false); load(); }} />
        </Dialog>
      }
    >
      <div className="tech-card overflow-hidden rise rise-1">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Custom roles</Th><Th>Status</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`user-row-${u.user_id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} className="h-8 w-8 rounded-md object-cover border" alt="" />
                      ) : (
                        <div className="h-8 w-8 rounded-md bg-neutral-100 grid place-items-center font-semibold text-neutral-700">{u.name?.[0]?.toUpperCase()}</div>
                      )}
                      <span className="font-medium">{u.name}</span>
                      {u.user_id === me?.user_id && <span className="text-[10px] uppercase tracking-wider text-neutral-500">(you)</span>}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-neutral-700">{u.email}</td>
                  <td className="py-3 px-4">
                    <Select
                      value={u.role}
                      onValueChange={(v) => updateUser(u.user_id, { role: v })}
                      disabled={u.role === "super_admin" && me?.role !== "super_admin"}
                    >
                      <SelectTrigger className="h-8 w-[150px]" data-testid={`user-role-${u.user_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        {me?.role === "super_admin" && <SelectItem value="super_admin">Super admin</SelectItem>}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-3 px-4">
                    <RoleEditor user={u} roles={roles} onSave={(ids) => updateUser(u.user_id, { custom_role_ids: ids })} />
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-[11px] rounded-sm font-medium ${u.is_active ? "status-done" : "status-blocked"}`}>
                      {u.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    {u.is_active && u.role !== "super_admin" && u.user_id !== me?.user_id && (
                      <button onClick={() => deactivate(u.user_id)} className="text-xs text-red-600 hover:underline" data-testid={`user-deactivate-${u.user_id}`}>
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

function Th({ children }) {
  return <th className="py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-neutral-600 text-left">{children}</th>;
}

function RoleEditor({ user, roles, onSave }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(user.custom_role_ids || []);
  useEffect(() => setSel(user.custom_role_ids || []), [user]);

  const toggle = (rid) => {
    setSel(prev => prev.includes(rid) ? prev.filter(x => x !== rid) : [...prev, rid]);
  };
  const save = () => { onSave(sel); setOpen(false); };

  const names = (user.custom_role_ids || []).map(id => roles.find(r => r.role_id === id)?.name).filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-left text-sm hover:underline" data-testid={`user-custom-roles-${user.user_id}`}>
          {names.length ? names.join(", ") : <span className="text-neutral-400">—</span>}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Custom roles for {user.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 mt-3">
          {roles.map(r => (
            <label key={r.role_id} className="flex items-center gap-3 p-2 hover:bg-neutral-50 rounded-md cursor-pointer">
              <Checkbox checked={sel.includes(r.role_id)} onCheckedChange={() => toggle(r.role_id)} data-testid={`role-toggle-${r.role_id}`} />
              <div>
                <div className="text-sm font-medium">{r.name}</div>
                <div className="text-xs text-neutral-500">{r.description}</div>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={save} data-testid="user-roles-save" className="bg-neutral-950 hover:bg-neutral-800 text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteDialog({ roles, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", role: "employee", custom_role_ids: [] });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setSubmitting(true);
    try {
      await api.post("/users/invite", form);
      toast.success("Member invited. They can sign in with Google using this email.");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <DialogContent className="sm:max-w-[480px]">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Invite member</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        <Field label="Full name">
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="invite-name" />
        </Field>
        <Field label="Work email">
          <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="invite-email" />
        </Field>
        <Field label="Role">
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Custom roles (optional)">
          <div className="space-y-1.5 border border-neutral-200 rounded-md p-2">
            {roles.length === 0 && <div className="text-xs text-neutral-500 p-2">No custom roles yet.</div>}
            {roles.map(r => (
              <label key={r.role_id} className="flex items-center gap-2.5 p-1.5 hover:bg-neutral-50 rounded-sm cursor-pointer">
                <Checkbox
                  checked={form.custom_role_ids.includes(r.role_id)}
                  onCheckedChange={() => setForm(f => ({
                    ...f,
                    custom_role_ids: f.custom_role_ids.includes(r.role_id)
                      ? f.custom_role_ids.filter(x => x !== r.role_id)
                      : [...f.custom_role_ids, r.role_id]
                  }))}
                  data-testid={`invite-role-${r.role_id}`}
                />
                <span className="text-sm">{r.name}</span>
              </label>
            ))}
          </div>
        </Field>
        <p className="text-xs text-neutral-500 flex items-center gap-1.5">
          <Mail className="h-3 w-3" /> Once invited, they sign in with Google using this exact email.
        </p>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={submitting} className="bg-neutral-950 hover:bg-neutral-800 text-white" data-testid="invite-submit">
          {submitting ? "Sending…" : "Send invite"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="overline text-[10px]">{label}</Label>
      {children}
    </div>
  );
}
